/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect } from "react";

import { clearSelection, selectAll } from "src/components/Shared/ContentEditable/lib/utils";

interface Props {
  value: string;
  onChange: (value: string) => void;
  onBlur: (e: React.FocusEvent) => void;
  autoFocus: boolean;
  editable: boolean;
  onKeyDownOverride?: (e: React.KeyboardEvent) => void;
  className?: string;
}

export const ContentEditable = ({
  value,
  onChange,
  editable,
  onBlur,
  onKeyDownOverride,
  autoFocus,
  className,
}: Props) => {
  const ref = React.useRef<HTMLSpanElement>(null);

  const defaultOnKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        ref.current?.blur();
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    if (autoFocus && ref.current) {
      selectAll(ref.current);
    }
  }, [ref.current]);

  const handleBlur = (e: React.FocusEvent) => {
    clearSelection();
    onBlur(e);
  };

  return (
    <span
      className={className}
      ref={ref}
      spellCheck={false}
      suppressContentEditableWarning
      contentEditable={editable}
      onInput={(e) => onChange(e.currentTarget.textContent || "")}
      onKeyDown={onKeyDownOverride || defaultOnKeyDown}
      onBlur={handleBlur}
    >
      {value}
    </span>
  );
};
